import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BRAND_NAME } from "@/brand";
import { friendlyAuthError } from "@/auth/authErrors";
import { PRIVACY_URL, TERMS_URL } from "@/auth/legalLinks";
import SitewrapLogo from "@/components/SitewrapLogo";
import "@/styles/login.css";
import "@/styles/sitewrap-logo.css";

type Step = "email" | "verify" | "totp";
type AuthMode = "signin" | "register";

function readInitialAuthMode(): AuthMode {
  if (typeof window === "undefined") return "signin";
  return window.location.pathname.replace(/\/+$/, "") === "/dashboard/signup" ? "register" : "signin";
}

function readGateDomain(): string {
  if (typeof window === "undefined") return "";
  try {
    const q = new URLSearchParams(window.location.search);
    return String(q.get("gate") || "").trim().toLowerCase();
  } catch {
    return "";
  }
}

export default function LoginPage() {
  const [mode, setMode] = useState<AuthMode>(() => readInitialAuthMode());
  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [digits, setDigits] = useState(["", "", "", ""]);
  const [totpDigits, setTotpDigits] = useState(["", "", "", "", "", ""]);
  const [mfaToken, setMfaToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const gateDomain = useMemo(() => readGateDomain(), []);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const totpRefs = useRef<(HTMLInputElement | null)[]>([]);
  const verifyingRef = useRef(false);

  const apiBase = gateDomain ? "/__gate/auth" : "/dashboard/api/auth";
  const canRegister = !gateDomain;

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const q = new URLSearchParams(window.location.search);
      if (q.get("mfa") === "1" && q.get("mfaToken")) {
        setMfaToken(String(q.get("mfaToken") || ""));
        setEmail(String(q.get("email") || ""));
        setStep("totp");
      }
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    document.title = `${BRAND_NAME} · ${mode === "register" ? "Sign up" : "Sign in"}`;
  }, [mode]);

  const sendLink = useCallback(async () => {
    const em = email.trim().toLowerCase();
    if (!em || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) {
      setErr("Enter a valid email address.");
      return;
    }
    if (mode === "register" && !acceptTerms) {
      setErr("Please accept the Terms & Conditions to create an account.");
      return;
    }

    setBusy(true);
    setErr("");
    setMsg("");
    try {
      const endpoint = mode === "register" && canRegister ? `${apiBase}/register` : `${apiBase}/magic-link`;
      const body =
        mode === "register" && canRegister
          ? { email: em, acceptTerms: true, ...(gateDomain ? { gate: gateDomain } : {}) }
          : { email: em, ...(gateDomain ? { gate: gateDomain } : {}) };

      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "content-type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify(body)
      });
      const data = (await res.json().catch(() => ({}))) as {
        ok?: boolean;
        error?: string;
        message?: string;
        registered?: boolean;
      };
      if (!res.ok || !data.ok) {
        throw new Error(friendlyAuthError(data.error, { message: data.message }));
      }
      setStep("verify");
      setMsg(
        data.message ||
          (mode === "register"
            ? "Check your email for a sign-in link and 4-digit code."
            : "Check your email for a sign-in link and 4-digit code.")
      );
      setDigits(["", "", "", ""]);
      setTimeout(() => inputRefs.current[0]?.focus(), 80);
    } catch (e) {
      setErr(String(e instanceof Error ? e.message : e));
    } finally {
      setBusy(false);
    }
  }, [apiBase, acceptTerms, canRegister, email, gateDomain, mode]);

  const verifyCode = useCallback(async (codeOverride?: string) => {
    if (verifyingRef.current) return;
    const em = email.trim().toLowerCase();
    const code = codeOverride ?? digits.join("");
    if (!/^\d{4}$/.test(code)) {
      setErr("Enter the 4-digit code from your email.");
      return;
    }
    verifyingRef.current = true;
    setBusy(true);
    setErr("");
    try {
      const res = await fetch(`${apiBase}/verify-code`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ email: em, code, ...(gateDomain ? { gate: gateDomain } : {}) })
      });
      const data = (await res.json().catch(() => ({}))) as {
        ok?: boolean;
        redirectUrl?: string;
        error?: string;
        message?: string;
        attemptsLeft?: number;
        mfaRequired?: boolean;
        mfaToken?: string;
      };
      if (!res.ok || !data.ok) {
        throw new Error(
          friendlyAuthError(data.error, { message: data.message, attemptsLeft: data.attemptsLeft })
        );
      }
      if (data.mfaRequired && data.mfaToken) {
        setMfaToken(data.mfaToken);
        setTotpDigits(["", "", "", "", "", ""]);
        setStep("totp");
        setMsg("Enter the 6-digit code from your authenticator app.");
        setTimeout(() => totpRefs.current[0]?.focus(), 80);
        return;
      }
      if (data.redirectUrl) {
        window.location.href = data.redirectUrl;
        return;
      }
      window.location.href = "/dashboard";
    } catch (e) {
      setErr(String(e instanceof Error ? e.message : e));
    } finally {
      verifyingRef.current = false;
      setBusy(false);
    }
  }, [apiBase, digits, email, gateDomain]);

  const verifyTotp = useCallback(async (codeOverride?: string) => {
    if (verifyingRef.current) return;
    const code = codeOverride ?? totpDigits.join("");
    if (!/^\d{6}$/.test(code) || !mfaToken) {
      setErr("Enter the 6-digit authenticator code.");
      return;
    }
    verifyingRef.current = true;
    setBusy(true);
    setErr("");
    try {
      const res = await fetch(`${apiBase}/verify-totp`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ mfaToken, code, ...(gateDomain ? { gate: gateDomain } : {}) })
      });
      const data = (await res.json().catch(() => ({}))) as {
        ok?: boolean;
        redirectUrl?: string;
        error?: string;
        message?: string;
      };
      if (!res.ok || !data.ok) {
        throw new Error(friendlyAuthError(data.error, { message: data.message }));
      }
      window.location.href = data.redirectUrl || "/dashboard";
    } catch (e) {
      setErr(String(e instanceof Error ? e.message : e));
    } finally {
      verifyingRef.current = false;
      setBusy(false);
    }
  }, [apiBase, gateDomain, mfaToken, totpDigits]);

  const switchMode = (next: AuthMode) => {
    setMode(next);
    setStep("email");
    setErr("");
    setMsg("");
    if (next === "signin") setAcceptTerms(false);
  };

  const onDigitChange = (idx: number, value: string) => {
    const v = value.replace(/\D/g, "").slice(-1);
    const next = [...digits];
    next[idx] = v;
    setDigits(next);
    if (v && idx < 3) inputRefs.current[idx + 1]?.focus();
    if (v && idx === 3 && /^\d{4}$/.test(next.join(""))) {
      void verifyCode(next.join(""));
    }
  };

  const onDigitKeyDown = (idx: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !digits[idx] && idx > 0) {
      inputRefs.current[idx - 1]?.focus();
    }
    if (e.key === "Enter" && step === "verify") void verifyCode();
  };

  const onPasteCode = (e: React.ClipboardEvent) => {
    const t = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 4);
    if (t.length === 4) {
      e.preventDefault();
      setDigits(t.split(""));
      inputRefs.current[3]?.focus();
      void verifyCode(t);
    }
  };

  return (
    <div className="loginPage">
      <div className="loginBackdrop" aria-hidden="true" />
      <main className="loginCard">
        <header className="loginBrand">
          <SitewrapLogo size={56} variant="gradient" />
          <p className="loginTagline">Website operations dashboard</p>
        </header>

        {gateDomain ? (
          <p className="loginGateNote">
            Signing in to unlock <span className="mono">{gateDomain}</span>
          </p>
        ) : null}

        {step === "email" ? (
          <>
            {canRegister ? (
              <div className="loginModeTabs" role="tablist" aria-label="Authentication mode">
                <button
                  type="button"
                  role="tab"
                  aria-selected={mode === "signin"}
                  className={"loginModeTab" + (mode === "signin" ? " loginModeTab--active" : "")}
                  onClick={() => switchMode("signin")}
                >
                  Sign in
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={mode === "register"}
                  className={"loginModeTab" + (mode === "register" ? " loginModeTab--active" : "")}
                  onClick={() => switchMode("register")}
                >
                  Sign up
                </button>
              </div>
            ) : null}

            <h1 className="loginTitle">{mode === "register" ? "Create your account" : "Sign in"}</h1>
            <p className="loginLead">
              {mode === "register"
                ? "Enter your work email. We'll send a magic link and a short code—no password needed."
                : "Enter your work email to receive a magic link and sign-in code."}
            </p>

            <form
              className="loginForm"
              onSubmit={(e) => {
                e.preventDefault();
                void sendLink();
              }}
            >
              <label className="loginLabel" htmlFor="login-email">
                Work email
              </label>
              <input
                id="login-email"
                type="email"
                className="loginInput mono"
                autoComplete="email"
                autoFocus
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={busy}
              />

              {mode === "register" && canRegister ? (
                <label className="loginTerms">
                  <input
                    type="checkbox"
                    className="loginTermsCheck"
                    checked={acceptTerms}
                    onChange={(e) => setAcceptTerms(e.target.checked)}
                    disabled={busy}
                  />
                  <span className="loginTermsText">
                    I accept the{" "}
                    <a href={TERMS_URL} target="_blank" rel="noopener noreferrer">
                      Terms &amp; Conditions
                    </a>{" "}
                    and{" "}
                    <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">
                      Privacy Policy
                    </a>
                    .
                  </span>
                </label>
              ) : null}

              {err ? <p className="loginErr">{err}</p> : null}
              <button
                type="submit"
                className="loginBtn primary"
                disabled={busy || (mode === "register" && !acceptTerms)}
              >
                {busy ? "Sending…" : mode === "register" ? "Create account" : "Continue"}
              </button>
            </form>

            {canRegister ? (
              <p className="loginSwitchHint">
                {mode === "signin" ? (
                  <>
                    New to {BRAND_NAME}?{" "}
                    <button type="button" className="loginInlineLink" onClick={() => switchMode("register")}>
                      Sign up
                    </button>
                  </>
                ) : (
                  <>
                    Already have an account?{" "}
                    <button type="button" className="loginInlineLink" onClick={() => switchMode("signin")}>
                      Sign in
                    </button>
                  </>
                )}
              </p>
            ) : null}
          </>
        ) : step === "totp" ? (
          <>
            <h1 className="loginTitle">Authenticator code</h1>
            <p className="loginSubtitle">
              Enter the 6-digit code from your authenticator app for <strong>{email.trim()}</strong>.
            </p>
            {msg ? <p className="loginOk">{msg}</p> : null}
            <div className="loginCodeRow" onPaste={(e) => {
              const t = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
              if (t.length === 6) {
                e.preventDefault();
                setTotpDigits(t.split(""));
                totpRefs.current[5]?.focus();
                void verifyTotp(t);
              }
            }}>
              {totpDigits.map((d, i) => (
                <input
                  key={i}
                  ref={(el) => {
                    totpRefs.current[i] = el;
                  }}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={1}
                  className="loginCodeBox"
                  value={d}
                  aria-label={`Authenticator digit ${i + 1}`}
                  disabled={busy}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, "").slice(-1);
                    const next = [...totpDigits];
                    next[i] = v;
                    setTotpDigits(next);
                    if (v && i < 5) totpRefs.current[i + 1]?.focus();
                    if (v && i === 5 && /^\d{6}$/.test(next.join(""))) void verifyTotp(next.join(""));
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Backspace" && !totpDigits[i] && i > 0) totpRefs.current[i - 1]?.focus();
                    if (e.key === "Enter") void verifyTotp();
                  }}
                />
              ))}
            </div>
            {err ? <p className="loginErr">{err}</p> : null}
            <button type="button" className="loginBtn primary" disabled={busy} onClick={() => void verifyTotp()}>
              {busy ? "Verifying…" : "Verify"}
            </button>
            <div className="loginActions">
              <button
                type="button"
                className="loginLinkBtn"
                disabled={busy}
                onClick={() => {
                  setStep("email");
                  setMfaToken("");
                  setErr("");
                  setMsg("");
                }}
              >
                Start over
              </button>
            </div>
          </>
        ) : (
          <>
            <h1 className="loginTitle">Check your email</h1>
            <p className="loginSubtitle">
              We sent a sign-in link and code to <strong>{email.trim()}</strong>. Open the link on any device, or
              enter the code below if you&apos;re on desktop.
            </p>
            {msg ? <p className="loginOk">{msg}</p> : null}
            <div className="loginCodeRow" onPaste={onPasteCode}>
              {digits.map((d, i) => (
                <input
                  key={i}
                  ref={(el) => {
                    inputRefs.current[i] = el;
                  }}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={1}
                  className="loginCodeBox"
                  value={d}
                  aria-label={`Digit ${i + 1}`}
                  disabled={busy}
                  onChange={(e) => onDigitChange(i, e.target.value)}
                  onKeyDown={(e) => onDigitKeyDown(i, e)}
                />
              ))}
            </div>
            {err ? <p className="loginErr">{err}</p> : null}
            <button type="button" className="loginBtn primary" disabled={busy} onClick={() => void verifyCode()}>
              {busy ? "Verifying…" : "Sign in with code"}
            </button>
            <div className="loginActions">
              <button type="button" className="loginLinkBtn" disabled={busy} onClick={() => void sendLink()}>
                Resend email
              </button>
              <button
                type="button"
                className="loginLinkBtn"
                disabled={busy}
                onClick={() => {
                  setStep("email");
                  setErr("");
                  setMsg("");
                }}
              >
                Use a different email
              </button>
            </div>
          </>
        )}

        <footer className="loginFooter">
          <p>Secure, passwordless access. Links expire in 15 minutes.</p>
        </footer>
      </main>
    </div>
  );
}
